import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CANVAS_WIDTH as DEFAULT_CANVAS_WIDTH,
  MAX_SPONSOR_WIDTH_RATIO,
  MIN_SPONSOR_WIDTH,
  clampSponsorRect,
  isSponsorInLowerHalf,
  snapSponsorRect,
} from '../utilities/sponsorPool';
import '../styles/sponsorOverlay.css';

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/** Convert screen pointer → canvas coords from the layer's live box (not React scale state). */
const clientToCanvas = (layer, clientX, clientY, canvasWidth, canvasHeight) => {
  if (!layer || !canvasHeight || !canvasWidth) return { x: 0, y: 0 };
  const rect = layer.getBoundingClientRect();
  const w = Math.max(0.0001, rect.width);
  const h = Math.max(0.0001, rect.height);
  return {
    x: ((clientX - rect.left) / w) * canvasWidth,
    y: ((clientY - rect.top) / h) * canvasHeight,
  };
};

const toPercent = (value, total) => `${(value / Math.max(1, total)) * 100}%`;

function SponsorOverlay({
  sponsors,
  selectedId,
  onSelect,
  onChange,
  canvasWidth = DEFAULT_CANVAS_WIDTH,
  canvasHeight,
  disabled = false,
}) {
  const layerRef = useRef(null);
  const dragRef = useRef(null);
  const sponsorsRef = useRef(sponsors);
  const canvasWidthRef = useRef(canvasWidth);
  const canvasHeightRef = useRef(canvasHeight);
  const onChangeRef = useRef(onChange);
  const [guides, setGuides] = useState({ vertical: [], horizontal: [] });

  sponsorsRef.current = sponsors;
  canvasWidthRef.current = canvasWidth;
  canvasHeightRef.current = canvasHeight;
  onChangeRef.current = onChange;

  const clearGuides = () => setGuides({ vertical: [], horizontal: [] });

  const updateSponsor = useCallback((id, patch, withSnap = false) => {
    const list = sponsorsRef.current;
    const width = canvasWidthRef.current;
    const height = canvasHeightRef.current;
    const current = list.find((item) => item.id === id);
    if (!current || !height || !width) return;
    let next = clampSponsorRect({ ...current, ...patch }, width, height);
    if (withSnap) {
      const snapped = snapSponsorRect(
        next,
        width,
        height,
        list.filter((item) => item.id !== id)
      );
      next = clampSponsorRect({ ...next, x: snapped.x, y: snapped.y }, width, height);
      setGuides(snapped.guides || { vertical: [], horizontal: [] });
    }
    onChangeRef.current(id, next);
  }, []);

  useEffect(() => {
    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      event.preventDefault();
      const width = canvasWidthRef.current;
      const height = canvasHeightRef.current;
      const layer = layerRef.current;
      if (!height || !width || !layer) return;

      if (drag.mode === 'move') {
        const clientX = event.touches?.[0]?.clientX ?? event.clientX;
        const clientY = event.touches?.[0]?.clientY ?? event.clientY;
        const point = clientToCanvas(layer, clientX, clientY, width, height);
        updateSponsor(drag.id, {
          x: point.x - drag.offsetX,
          y: point.y - drag.offsetY,
        }, true);
        return;
      }

      if (drag.mode === 'resize') {
        const clientX = event.touches?.[0]?.clientX ?? event.clientX;
        const clientY = event.touches?.[0]?.clientY ?? event.clientY;
        const point = clientToCanvas(layer, clientX, clientY, width, height);
        const aspect = drag.aspect || 1;
        let nextWidth = Math.max(MIN_SPONSOR_WIDTH, point.x - drag.originX);
        nextWidth = Math.min(width * MAX_SPONSOR_WIDTH_RATIO, nextWidth);
        let nextHeight = nextWidth / aspect;
        if (drag.originY + nextHeight > height) {
          nextHeight = Math.max(MIN_SPONSOR_WIDTH / aspect, height - drag.originY);
          nextWidth = nextHeight * aspect;
        }
        updateSponsor(drag.id, {
          x: drag.originX,
          y: drag.originY,
          width: nextWidth,
          height: nextHeight,
        }, false);
        return;
      }

      if (drag.mode === 'pinch' && event.touches && event.touches.length >= 2) {
        const t0 = event.touches[0];
        const t1 = event.touches[1];
        const nextDist = dist(
          { x: t0.clientX, y: t0.clientY },
          { x: t1.clientX, y: t1.clientY }
        );
        const ratio = nextDist / Math.max(1, drag.startDist);
        let nextWidth = Math.max(MIN_SPONSOR_WIDTH, drag.startWidth * ratio);
        nextWidth = Math.min(width * MAX_SPONSOR_WIDTH_RATIO, nextWidth);
        const nextHeight = nextWidth / drag.aspect;
        updateSponsor(drag.id, {
          width: nextWidth,
          height: nextHeight,
          x: drag.centerX - nextWidth / 2,
          y: drag.centerY - nextHeight / 2,
        }, false);
      }
    };

    const onPointerUp = () => {
      if (!dragRef.current) return;
      const { id } = dragRef.current;
      dragRef.current = null;
      clearGuides();
      // Final clamp after scale/layout may have settled mid-drag.
      const width = canvasWidthRef.current;
      const height = canvasHeightRef.current;
      const current = sponsorsRef.current.find((item) => item.id === id);
      if (current && height && width) {
        const clamped = clampSponsorRect(current, width, height);
        if (
          clamped.x !== current.x
          || clamped.y !== current.y
          || clamped.width !== current.width
          || clamped.height !== current.height
        ) {
          onChangeRef.current(id, clamped);
        }
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
    window.addEventListener('touchcancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
      window.removeEventListener('touchcancel', onPointerUp);
    };
  }, [updateSponsor]);

  if (!canvasHeight || canvasHeight <= 0 || !canvasWidth) return null;

  return (
    <div
      className={`ig-sponsor-layer${disabled ? ' is-disabled' : ''}`}
      ref={layerRef}
      onPointerDown={(event) => {
        if (event.target === layerRef.current) onSelect(null);
      }}
    >
      {guides.vertical.map((x) => (
        <div
          key={`vg-${x}`}
          className="ig-sponsor-guide is-vertical"
          style={{ left: toPercent(x, canvasWidth) }}
        />
      ))}
      {guides.horizontal.map((y) => (
        <div
          key={`hg-${y}`}
          className="ig-sponsor-guide is-horizontal"
          style={{ top: toPercent(y, canvasHeight) }}
        />
      ))}

      {sponsors.map((item) => {
        const selected = item.id === selectedId;
        const bottomAnchored = isSponsorInLowerHalf(item, canvasHeight);
        return (
          <div
            key={item.id}
            className={`ig-sponsor-item${selected ? ' is-selected' : ''}${bottomAnchored ? ' is-bottom-anchor' : ''}`}
            style={{
              left: toPercent(item.x, canvasWidth),
              top: toPercent(item.y, canvasHeight),
              width: toPercent(item.width, canvasWidth),
              height: toPercent(item.height, canvasHeight),
            }}
            onPointerDown={(event) => {
              if (disabled) return;
              event.stopPropagation();
              event.preventDefault();
              onSelect(item.id);
              if (event.pointerType === 'touch' && event.target?.closest?.('.ig-sponsor-handle')) {
                return;
              }
              const point = clientToCanvas(
                layerRef.current,
                event.clientX,
                event.clientY,
                canvasWidth,
                canvasHeight
              );
              dragRef.current = {
                mode: 'move',
                id: item.id,
                offsetX: point.x - item.x,
                offsetY: point.y - item.y,
              };
              try {
                event.currentTarget.setPointerCapture?.(event.pointerId);
              } catch (error) {
                // ignore
              }
            }}
            onTouchStart={(event) => {
              if (disabled) return;
              if (event.touches.length < 2) return;
              event.preventDefault();
              event.stopPropagation();
              onSelect(item.id);
              const t0 = event.touches[0];
              const t1 = event.touches[1];
              dragRef.current = {
                mode: 'pinch',
                id: item.id,
                aspect: item.aspect,
                startDist: dist(
                  { x: t0.clientX, y: t0.clientY },
                  { x: t1.clientX, y: t1.clientY }
                ),
                startWidth: item.width,
                centerX: item.x + item.width / 2,
                centerY: item.y + item.height / 2,
              };
            }}
          >
            <img src={item.src} alt={item.name} draggable={false} />
            {bottomAnchored ? (
              <span className="ig-sponsor-anchor-badge" title="bottom">↓</span>
            ) : null}
            {selected && !disabled ? (
              <button
                type="button"
                className="ig-sponsor-handle"
                aria-label="Resize"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  dragRef.current = {
                    mode: 'resize',
                    id: item.id,
                    aspect: item.aspect,
                    originX: item.x,
                    originY: item.y,
                  };
                  try {
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                  } catch (error) {
                    // ignore
                  }
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default SponsorOverlay;
